/**
 * Store de `beforeinstallprompt` / `appinstalled` (Chromium en Android; brief §2).
 *
 * Los eventos se escuchan **al cargar el módulo**, no dentro de un `useEffect`: Chrome puede
 * emitir `beforeinstallprompt` antes del primer render y, si nadie lo captura, el botón propio
 * nunca aparece. Por eso `main.tsx` importa este módulo antes de montar la app. React lo consume
 * con `useSyncExternalStore` (`use-install-prompt.ts`).
 */

/** El navegador no expone un tipo para este evento no estándar; se declara lo que se usa. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

function isBeforeInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return typeof (event as Partial<BeforeInstallPromptEvent>).prompt === 'function';
}

type Listener = () => void;

const listeners = new Set<Listener>();
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function onBeforeInstallPrompt(event: Event): void {
  // Sin preventDefault() el navegador muestra su propio mini-infobar; usamos nuestro botón.
  event.preventDefault();
  if (isBeforeInstallPromptEvent(event)) {
    deferred = event;
    emit();
  }
}

function onAppInstalled(): void {
  deferred = null;
  installed = true;
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

export const installPromptStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getPrompt: (): BeforeInstallPromptEvent | null => deferred,
  wasInstalled: (): boolean => installed,
  /** El evento solo sirve una vez: tras `prompt()` se descarta. */
  consumePrompt(): void {
    deferred = null;
    emit();
  },
  /** Solo pruebas: vuelve al estado inicial. */
  reset(): void {
    deferred = null;
    installed = false;
    emit();
  },
};

/** Abre el diálogo nativo de instalación y espera la decisión del usuario. */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = installPromptStore.getPrompt();
  if (!event) {
    return 'unavailable';
  }
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    installPromptStore.consumePrompt();
    return outcome;
  } catch {
    // El navegador rechazó el prompt (ya usado, sin gesto del usuario…).
    installPromptStore.consumePrompt();
    return 'unavailable';
  }
}
